export type FluidClickAnimationSettings = {
  actionScaleAmount: number;
  direction: number;
  durationSeconds: number;
  firstFallEnd: number;
  firstFallStart: number;
  firstRiseEnd: number;
  firstRiseStart: number;
  intensity: number;
  lift: number;
  rotationXImpulse: number;
  rotationXMagnitude: number;
  rotationYMultiplier: number;
  rotationZMultiplier: number;
  secondFallEnd: number;
  secondFallStart: number;
  secondRiseEnd: number;
  secondRiseStart: number;
  secondStrength: number;
  shift: number;
  stopEnd: number;
  stopStart: number;
  tilt: number;
  xMultiplier: number;
  yLiftMultiplier: number;
};

export type SpinClickAnimationSettings = {
  actionScaleAmount: number;
  durationSeconds: number;
  flutterFadeEndProgress: number;
  flutterFrequency: number;
  flutterRotationX: number;
  flutterRotationY: number;
  lift: number;
  rotations: number;
  rotationXAmplitude: number;
  rotationZAmount: number;
  rotationZFrequency: number;
  scaleUpDurationProgress: number;
  settleStartProgress: number;
};

export type RevealClickAnimationSettings = SpinClickAnimationSettings & {
  fadeEndMs: number;
  fadeStartMs: number;
  growScale: number;
  revealOnsetMs: number;
  spinEaseX1: number;
  spinEaseX2: number;
  spinEaseY1: number;
  spinEaseY2: number;
  spinGrowStartProgress: number;
  targetOffsetXPercent: number;
  targetOffsetYPercent: number;
  targetRotationTurnsOffset: number;
  targetScaleMultiplier: number;
  transitionDurationMs: number;
  transitionStartMs: number;
};

export type PackSpringSettings = {
  bounce?: number;
  damping?: number;
  mass?: number;
  stiffness?: number;
  visualDuration?: number;
};

export type RewardAnimationSettings = {
  durationMs: number;
  easeX1: number;
  easeX2: number;
  easeY1: number;
  easeY2: number;
  footerDelayMs: number;
  gradientBackgroundSizePercent: number;
  gradientDelayMs: number;
  gradientDurationMs: number;
  gradientEndPercent: number;
  gradientOpacity: number;
  gradientStartPercent: number;
  heroDelayMs: number;
  overshootPercent: number;
  overshootScale: number;
  overshootTranslateX: number;
  overshootTranslateY: number;
  packSpring: PackSpringSettings;
  rewardImagesDelayMs: number;
  secondaryDelayMs: number;
  settlePercent: number;
  settleScale: number;
  settleTranslateX: number;
  settleTranslateY: number;
  staggerMs: number;
  startScale: number;
  startTranslateX: number;
  startTranslateY: number;
  titleDelayMs: number;
};

export type RewardImageLayoutSettings = {
  offsetX: number;
  offsetY: number;
  rotationDeg: number;
  widthPercent: number;
};

export type RewardImagesLayoutSettings = {
  doubleYellow: RewardImageLayoutSettings;
  legendary: RewardImageLayoutSettings;
  plantBlue: RewardImageLayoutSettings;
  plantSilhouette: RewardImageLayoutSettings;
  sign: RewardImageLayoutSettings;
};

export type GardenImageLayoutSettings = {
  leftPercent: number;
  /**
   * How much of the available middle band (title → tagline) the garden fills.
   * 100 = max fill without colliding with the tagline.
   */
  sizePercent: number;
  topPercent: number;
};

export type AnimationTuningSettings = {
  clickFour: RevealClickAnimationSettings;
  clickOne: FluidClickAnimationSettings;
  clickThree: SpinClickAnimationSettings;
  clickTwo: FluidClickAnimationSettings;
  gardenImage: GardenImageLayoutSettings;
  reward: RewardAnimationSettings;
  rewardImages: RewardImagesLayoutSettings;
};

const defaultActionScaleAmount = 0.064_999_999_999_999_95;

const defaultFluidClickSettings = {
  actionScaleAmount: defaultActionScaleAmount,
  durationSeconds: 1.18,
  firstFallEnd: 0.34,
  firstFallStart: 0.22,
  firstRiseEnd: 0.16,
  firstRiseStart: 0.03,
  lift: 0.28,
  rotationXImpulse: 0.2,
  rotationXMagnitude: -0.08,
  rotationYMultiplier: 1.12,
  rotationZMultiplier: -0.92,
  secondFallEnd: 0.68,
  secondFallStart: 0.52,
  secondRiseEnd: 0.44,
  secondRiseStart: 0.3,
  secondStrength: 0.94,
  shift: 0.16,
  stopEnd: 0.78,
  stopStart: 0.62,
  tilt: 0.24,
  xMultiplier: 1.08,
  yLiftMultiplier: 0.42,
} satisfies Omit<FluidClickAnimationSettings, "direction" | "intensity">;

const defaultSpinClickSettings = {
  actionScaleAmount: defaultActionScaleAmount,
  durationSeconds: 1.58,
  flutterFadeEndProgress: 0.58,
  flutterFrequency: 3.2,
  flutterRotationX: 0.08,
  flutterRotationY: 0.22,
  lift: 0.34,
  rotations: 3,
  rotationXAmplitude: 0.28,
  rotationZAmount: 0.12,
  rotationZFrequency: 1.4,
  scaleUpDurationProgress: 0.16,
  settleStartProgress: 0.72,
} satisfies SpinClickAnimationSettings;

export const defaultAnimationTuningSettings = {
  clickFour: {
    ...defaultSpinClickSettings,
    durationSeconds: 1.910_000_000_000_000_1,
    fadeEndMs: 1670,
    fadeStartMs: 1600,
    growScale: 2.25,
    revealOnsetMs: 2320,
    spinEaseX1: 0.165,
    spinEaseX2: 0.44,
    spinEaseY1: 0.84,
    spinEaseY2: 1,
    spinGrowStartProgress: 0.08,
    targetOffsetXPercent: 0,
    targetOffsetYPercent: -4.6,
    targetRotationTurnsOffset: 1,
    targetScaleMultiplier: 1.32,
    transitionDurationMs: 1050,
    transitionStartMs: 1120,
  },
  clickOne: {
    ...defaultFluidClickSettings,
    actionScaleAmount: 0.072_999_999_999_999_95,
    direction: -0.75,
    durationSeconds: 0.619_999_999_999_999_9,
    intensity: 0.53,
    lift: 0.045_000_000_000_000_02,
    rotationXImpulse: 0.205_000_000_000_000_02,
    rotationXMagnitude: -0.265,
    rotationYMultiplier: 0.240_000_000_000_000_07,
    rotationZMultiplier: -1.32,
    shift: 0.22,
    tilt: 0.099_999_999_999_999_99,
    xMultiplier: 0.370_000_000_000_000_05,
    yLiftMultiplier: 0.73,
  },
  clickThree: {
    ...defaultSpinClickSettings,
  },
  clickTwo: {
    ...defaultFluidClickSettings,
    actionScaleAmount: 0.180_999_999_999_999_94,
    direction: 0.149_999_999_999_999_97,
    durationSeconds: 0.669_999_999_999_999_9,
    intensity: 1.08,
    lift: 0.45,
    rotationXImpulse: 0.2,
    rotationXMagnitude: -0.08,
    rotationYMultiplier: 1.12,
    rotationZMultiplier: -0.92,
    shift: 0.235_000_000_000_000_01,
    tilt: 0.355_000_000_000_000_04,
    xMultiplier: 1.08,
    yLiftMultiplier: 0.119_999_999_999_999_98,
  },
  gardenImage: {
    leftPercent: 50,
    sizePercent: 116,
    topPercent: 14.171_875,
  },
  reward: {
    durationMs: 540,
    easeX1: 0,
    easeX2: 0.22,
    easeY1: 1.42,
    easeY2: 1,
    footerDelayMs: 2860,
    gradientBackgroundSizePercent: 240,
    gradientDelayMs: 380,
    gradientDurationMs: 2300,
    gradientEndPercent: 100,
    gradientOpacity: 0.72,
    gradientStartPercent: 0,
    heroDelayMs: 1640,
    overshootPercent: 54,
    overshootScale: 1,
    overshootTranslateX: 0,
    overshootTranslateY: -0.45,
    packSpring: {
      damping: 18,
      mass: 0.9,
      stiffness: 220,
    },
    rewardImagesDelayMs: 2140,
    secondaryDelayMs: 2140,
    settlePercent: 76,
    settleScale: 1,
    settleTranslateX: 0,
    settleTranslateY: 0.138_888_9,
    staggerMs: 155,
    startScale: 1,
    startTranslateX: 0,
    startTranslateY: 3.055_555_6,
    titleDelayMs: 1520,
  },
  rewardImages: {
    doubleYellow: {
      offsetX: 0,
      offsetY: 0,
      rotationDeg: 0,
      widthPercent: 19.945_532_7,
    },
    legendary: {
      offsetX: 0,
      offsetY: 0,
      rotationDeg: 0,
      widthPercent: 20.855_126_8,
    },
    plantBlue: {
      offsetX: 0,
      offsetY: 0,
      rotationDeg: 0,
      widthPercent: 17.010_301_1,
    },
    plantSilhouette: {
      offsetX: -3.2,
      offsetY: 3.35,
      rotationDeg: 0,
      widthPercent: 8.994_237_7,
    },
    sign: {
      offsetX: 0,
      offsetY: 0,
      rotationDeg: 0,
      widthPercent: 10.709_344_1,
    },
  },
} satisfies AnimationTuningSettings;

export function cloneAnimationTuningSettings(
  settings: AnimationTuningSettings,
): AnimationTuningSettings {
  return {
    clickFour: { ...settings.clickFour },
    clickOne: { ...settings.clickOne },
    clickThree: { ...settings.clickThree },
    clickTwo: { ...settings.clickTwo },
    gardenImage: { ...settings.gardenImage },
    reward: { ...settings.reward, packSpring: { ...settings.reward.packSpring } },
    rewardImages: {
      doubleYellow: { ...settings.rewardImages.doubleYellow },
      legendary: { ...settings.rewardImages.legendary },
      plantBlue: { ...settings.rewardImages.plantBlue },
      plantSilhouette: { ...settings.rewardImages.plantSilhouette },
      sign: { ...settings.rewardImages.sign },
    },
  };
}

export function assignAnimationTuningSettings(
  target: AnimationTuningSettings,
  source: AnimationTuningSettings,
) {
  Object.assign(target.clickFour, source.clickFour);
  Object.assign(target.clickOne, source.clickOne);
  Object.assign(target.clickThree, source.clickThree);
  Object.assign(target.clickTwo, source.clickTwo);
  Object.assign(target.gardenImage, source.gardenImage);
  Object.assign(target.reward, source.reward);
  target.reward.packSpring = { ...source.reward.packSpring };
  Object.assign(target.rewardImages.doubleYellow, source.rewardImages.doubleYellow);
  Object.assign(target.rewardImages.legendary, source.rewardImages.legendary);
  Object.assign(target.rewardImages.plantBlue, source.rewardImages.plantBlue);
  Object.assign(target.rewardImages.plantSilhouette, source.rewardImages.plantSilhouette);
  Object.assign(target.rewardImages.sign, source.rewardImages.sign);
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum);
}

function formatNumber(value: number) {
  return Number(value.toFixed(6)).toString();
}

function formatResponsiveLength(valueCqw: number) {
  const valueCqh = valueCqw * 1.406_25;
  const comparison = valueCqw < 0 ? "max" : "min";

  return `${comparison}(${formatNumber(valueCqw)}cqw, ${formatNumber(valueCqh)}cqh)`;
}

function formatRewardTranslate(x: number, y: number) {
  return `${formatResponsiveLength(x)} ${formatResponsiveLength(y)}`;
}

export function createRewardAnimationKeyframes(settings: RewardAnimationSettings) {
  const overshootPercent = clamp(settings.overshootPercent, 1, 98);
  const settlePercent = clamp(
    Math.max(settings.settlePercent, overshootPercent + 1),
    overshootPercent + 1,
    99,
  );

  return `
@keyframes reveal-reward-item-pop {
  0% {
    opacity: 0;
    scale: ${formatNumber(settings.startScale)};
    translate: ${formatRewardTranslate(settings.startTranslateX, settings.startTranslateY)};
  }

  ${formatNumber(overshootPercent)}% {
    opacity: 1;
    scale: ${formatNumber(settings.overshootScale)};
    translate: ${formatRewardTranslate(settings.overshootTranslateX, settings.overshootTranslateY)};
  }

  ${formatNumber(settlePercent)}% {
    opacity: 1;
    scale: ${formatNumber(settings.settleScale)};
    translate: ${formatRewardTranslate(settings.settleTranslateX, settings.settleTranslateY)};
  }

  100% {
    opacity: 1;
    scale: 1;
    translate: 0 0;
  }
}

@keyframes legendary-question-gradient-flow {
  0% {
    background-position: 50% ${formatNumber(settings.gradientStartPercent)}%;
  }

  100% {
    background-position: 50% ${formatNumber(settings.gradientEndPercent)}%;
  }
}
`;
}
